import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#4f46e5', '#10b981'];

const StudentProgress = () => {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({ course: '', age_range: '', country: '', county: '', search: '', sort: '' });
  const [loading, setLoading] = useState(true);
  const [expandedStudent, setExpandedStudent] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const role = user?.app_metadata?.role;
      if (role !== 'admin') navigate('/unauthorized');
    };
    checkAdmin();
  }, [navigate]);

  const uniqueValues = (key) => [...new Set(data.map(d => d[key]).filter(Boolean))];

  const fetchProgress = async () => {
    const { data: progressData, error } = await supabase.from('student_progress_view').select('*');
    if (error) console.error('Fetch error:', error);
    else setData(progressData);
    setLoading(false);
  };

  useEffect(() => {
    fetchProgress();
  }, []);

  const filteredData = data
    .filter(d => {
      return (
        (!filters.course || d.course_title === filters.course) &&
        (!filters.age_range || d.age_range === filters.age_range) &&
        (!filters.country || d.country === filters.country) &&
        (!filters.county || d.county === filters.county) &&
        (!filters.search || (d.full_name && d.full_name.toLowerCase().includes(filters.search.toLowerCase())) || (d.email && d.email.toLowerCase().includes(filters.search.toLowerCase())))
      );
    })
    .sort((a, b) => {
      if (filters.sort === 'progress') return b.progress_percent - a.progress_percent;
      if (filters.sort === 'score') return (b.final_score || 0) - (a.final_score || 0);
      return 0;
    });

  const students = Array.from(new Map(filteredData.map(d => [d.user_id, d])).values());
  const total = students.length;

  const avgProgress = total ? Math.round(filteredData.reduce((sum, d) => sum + d.progress_percent, 0) / total) : 0;
  const avgScore = total ? Math.round(filteredData.reduce((sum, d) => sum + (d.final_score || 0), 0) / total) : 0;

  const pieData = [
    { name: 'Completed', value: filteredData.filter(d => d.status === 'completed').length },
    { name: 'In Progress', value: filteredData.filter(d => d.status === 'in_progress').length },
  ];

  const barData = uniqueValues('course_title').map(title => {
    const courseItems = filteredData.filter(d => d.course_title === title);
    const avg = courseItems.length ? courseItems.reduce((sum, d) => sum + (d.final_score || 0), 0) / courseItems.length : 0;
    return { name: title, score: Math.round(avg) };
  });

  if (loading) return <p className="p-6">Loading...</p>;

  return (
    <div className="p-6 bg-white rounded-xl shadow">
      <h2 className="text-2xl font-semibold text-indigo-700 mb-6">Student Progress</h2>

      {/* Summary */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-indigo-50 p-4 rounded">
          <p className="text-gray-500">Total Learners</p>
          <p className="text-xl font-bold text-indigo-700">{total}</p>
        </div>
        <div className="bg-indigo-50 p-4 rounded">
          <p className="text-gray-500">Avg. Progress</p>
          <p className="text-xl font-bold text-indigo-700">{avgProgress}%</p>
        </div>
        <div className="bg-indigo-50 p-4 rounded">
          <p className="text-gray-500">Avg. Final Score</p>
          <p className="text-xl font-bold text-indigo-700">{avgScore}%</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barData} layout="vertical">
            <XAxis type="number" domain={[0, 100]} hide />
            <YAxis dataKey="name" type="category" width={150} />
            <Tooltip />
            <Bar dataKey="score" fill="#4f46e5" barSize={20} />
          </BarChart>
        </ResponsiveContainer>

        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              fill="#8884d8"
              label
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <select onChange={e => setFilters({ ...filters, course: e.target.value })} className="border px-3 py-2 rounded">
          <option value="">All Courses</option>
          {uniqueValues('course_title').map((course, i) => <option key={i} value={course}>{course}</option>)}
        </select>

        <select onChange={e => setFilters({ ...filters, age_range: e.target.value })} className="border px-3 py-2 rounded">
          <option value="">All Ages</option>
          {uniqueValues('age_range').map((age, i) => <option key={i} value={age}>{age}</option>)}
        </select>

        <select onChange={e => setFilters({ ...filters, country: e.target.value })} className="border px-3 py-2 rounded">
          <option value="">All Countries</option>
          {uniqueValues('country').map((country, i) => <option key={i} value={country}>{country}</option>)}
        </select>

        <select onChange={e => setFilters({ ...filters, county: e.target.value })} className="border px-3 py-2 rounded">
          <option value="">All Counties</option>
          {uniqueValues('county').map((county, i) => <option key={i} value={county}>{county}</option>)}
        </select>

        <input
          type="text"
          placeholder="Search student..."
          className="border px-3 py-2 rounded"
          onChange={e => setFilters({ ...filters, search: e.target.value })}
        />

        <select onChange={e => setFilters({ ...filters, sort: e.target.value })} className="border px-3 py-2 rounded">
          <option value="">Sort By</option>
          <option value="progress">Progress %</option>
          <option value="score">Final Score</option>
        </select>
      </div>

      {/* Collapsible by Student */}
      <div className="space-y-4">
        {students.map((student, idx) => {
          const studentCourses = filteredData.filter(d => d.user_id === student.user_id);
          return (
            <div key={idx} className="border rounded-lg">
              <button
                className="w-full text-left px-4 py-3 bg-indigo-50 hover:bg-indigo-100 rounded-t flex justify-between items-center"
                onClick={() => setExpandedStudent(expandedStudent === student.user_id ? null : student.user_id)}
              >
                <div>
                  <p className="font-semibold text-indigo-800">{student.full_name || 'Unnamed'} ({student.email || 'No Email'})</p>
                  <p className="text-sm text-gray-600">{student.country || 'N/A'} — Age: {student.age_range || 'N/A'}</p>
                </div>
                <span className="text-indigo-600">{expandedStudent === student.user_id ? '▲' : '▼'}</span>
              </button>
              {expandedStudent === student.user_id && (
                <table className="w-full text-sm border-t">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 border">Course</th>
                      <th className="p-2 border">Progress</th>
                      <th className="p-2 border">Final Score</th>
                      <th className="p-2 border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentCourses.map((course, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2 border">{course.course_title}</td>
                        <td className="p-2 border">{course.progress_percent}%</td>
                        <td className="p-2 border">{course.final_score ?? 'N/A'}</td>
                        <td className="p-2 border">{course.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudentProgress;
